// ============================================================
// Typed accessor over the headless engine bridge (window.__gpEngine).
// Components read derived data + mutate through this — they must NOT
// reimplement roll-ups / counts / RFI logic (see CLAUDE.md).
// ============================================================
import type { Ballyroe } from "@/types";

type Fn = (...args: any[]) => any;

export interface Engine {
  // data stores
  inspections: Record<string, any>;
  tableStatus: Record<string, any>;
  stationInspections: Record<string, any>;
  siteInspection: any;
  rfis: Record<string, any>;
  rfiSeq: number;
  ncrSeq: number;
  ncrRef: (n: number) => string;
  tddDocs: any[];
  controlsData: Record<string, any>;
  // session / view state (engine's mutable `state` object)
  state: any;
  currentRole: string;
  isLoggedIn: boolean;
  theme: string;
  setRole(r: string): void;
  login(): void;
  // static reference data + maps
  D: Ballyroe;
  records: any[];
  STATUS: Record<string, { label: string; color: string; glyph: string }>;
  RFI_STATUS: Record<string, { label: string; color: string; glyph: string }>;
  RFI_OUTCOME: Record<string, { label: string; color: string }>;
  C: Record<string, string>;
  // utils
  esc: (s: any) => string;
  fmt: (n: number) => string;
  fmtDate: Fn;
  fmtDateTime: Fn;
  fmtTime: Fn;
  toast: (msg: string, type?: string) => void;
  // computed
  qcCounts: Fn;
  allResults: Fn;
  fieldStats: Fn;
  siteCounts: Fn;
  punchItems: Fn;
  punchByCat: Fn;
  punchByLocation: Fn;
  punchLocKey: Fn;
  punchCounts: Fn;
  punchPinsForMap: Fn;
  pinColorFor: Fn;
  tableEffectiveStatus: Fn;
  recStarted: Fn;
  itemStarted: Fn;
  tableRoll: Fn;
  moduleRoll: Fn;
  moduleTableRoll: Fn;
  pileRoll: Fn;
  pileTableRoll: Fn;
  pileInfo: Fn;
  stationInvRoll: Fn;
  stationRoll: Fn;
  siteRoll: Fn;
  rowsForField: Fn;
  tablesInRow: Fn;
  tableTypeFor: Fn;
  /** Swap the in-memory topology (D.fields + per-table types) for the real CAD layout. */
  applyTopology: (payload: { fields: any[]; typeByKey?: Record<string, string> }) => boolean;
  /** Swap the synthetic station/inverter model for the real CAD transformer stations + designators. */
  applyInverterTopology: (payload: { stations: any[] }) => boolean;
  /** Real inverter designator "TR{n}-INV{m}" for a (stationId, invIdx); falls back to "<ST>-INV<n>". */
  invLabel: Fn;
  /** Short "INV{m}" part of the inverter designator for compact UI. */
  invShort: Fn;
  getInsp: Fn;
  itemResult: Fn;
  getModule: Fn;
  modItemResult: Fn;
  getPile: Fn;
  pileItemResult: Fn;
  getStationInv: Fn;
  stationInvItemResult: Fn;
  siteItemResult: Fn;
  rfiList: Fn;
  rfiCounts: Fn;
  rfiTargetLabel: Fn;
  rfiSuggestedOutcome: Fn;
  scopeBadge: Fn;
  storageUsageKB: Fn;
  checkStorageHealth: Fn;
  // mutations
  Store: Record<string, Fn>;
  setItemStatus: Fn;
  setTableAll: Fn;
  updItem: Fn;
  setSiteItem: Fn;
  setStationInvItem: Fn;
  pushHistory: Fn;
  undo: Fn;
  redo: Fn;
  applyTheme: Fn;
  toggleTheme: Fn;
  saveToStorage: Fn;
  loadFromStorage: Fn;
  exportSnapshot: Fn;
  importSnapshot: Fn;
  processImage: Fn;
  rfiOpenInspection: Fn;
  exportReport: Fn;
  exportFieldReport: Fn;
  exportStationReport: Fn;
  exportSiteReport: Fn;
  exportExecutiveReport: Fn;
  exportPunchPDF: Fn;
  exportPunchExcel: Fn;
  // legacy renderers (LegacyView fallback)
  chrome: Fn;
  navTabsHTML: Fn;
  vOverview: Fn;
  vRFI: Fn;
  vSiteMap: Fn;
  vQC: Fn;
  vPunch: Fn;
  vTDD: Fn;
  wire: Fn;
  refreshBadges: Fn;
  rerenderView: Fn;
  rerenderQC: Fn;
  rerenderRFI: Fn;
  setView: Fn;
  showLogin: Fn;
}

/** The headless engine bridge. Throws if the engine module hasn't loaded. */
export function eng(): Engine {
  const e = (window as any).__gpEngine as Engine | undefined;
  if (!e) throw new Error("Engine not loaded");
  return e;
}

export type Role = {
  label: string;
  operator: string;
  color: string;
  canEdit: boolean;
  canApprove: boolean;
  tddAccess: boolean;
  canCloseout?: boolean;
  desc: string;
};

/** The active role's permission flags. */
export function activeRole(): Role {
  const e = eng();
  return e.D.roles[e.currentRole as keyof typeof e.D.roles] as Role;
}
